import { Box, Card, Stack, Typography, getSpacing } from "@compass/core";
import {
  AprResponseActivitiesAndServices,
  AprResponse as AprResponseModel,
  AprSection,
  AprSectionId,
  AprSectionItemId,
} from "generated/openApi";

import "moment-timezone";
import { StudentParticipation } from "./SectionIV/StudentParticipation";
import { StudentParticipationByEthnicBackground } from "./SectionIV/StudentParticipationByEthnicBackground";
import { StudentParticipationByGender } from "./SectionIV/StudentParticipationByGender";
import { StudentParticipationByLep } from "./SectionIV/StudentParticipationByLep";
import { StudentParticipationByIep } from "./SectionIV/StudentParticipationByIep";
import { StudentsByShelter } from "./SectionIV/StudentsByShelter";
import { StudentsByAnotherFederallyFundedProgram } from "./SectionV/StudentsByAnotherFederallyFundedProgram";
import { ActivitiesAndServices } from "./SectionV/ActivitiesAndServices";
import { StudentActivitiesAndServices } from "./SectionV/StudentActivitiesAndServices";
import { CoreActivitiesProvidedToFamilyMembers } from "./SectionV/CoreActivitiesProvidedToFamilyMembers";
import { ProfessionalDevelopmentActivities } from "./SectionV/ProfessionalDevelopmentActivities";
import { AlgebraI } from "./SectionVI/AlgebraI";
import { AcademicEngagement } from "./SectionVI/AcademicEngagement";
import { FafsaCompletion } from "./SectionVI/FafsaCompletion";
import { HighSchoolGraduation } from "./SectionVI/HighSchoolGraduation";
import { PostsecondaryEnrollment } from "./SectionVII/PostsecondaryEnrollment";

export const AprResponse = (props: {
  response: AprResponseModel;
  aprSections: AprSection[];
  selectedSectionItemIds: number[];
}) => {
  const hasSection = (id: number) => {
    const section = props.aprSections?.find((x) => x.id === id);
    return section?.items?.map((x) => x.id).some((x) => props.selectedSectionItemIds.includes(x));
  };

  const getSectionName = (id: number) => {
    const section = props.aprSections?.find((x) => x.id === id);
    return section?.name;
  };

  const getSectionItemName = (id: number) => {
    return props.aprSections.flatMap((x) => x.items).find((y) => y?.id === id)?.name || "";
  };

  const sectionIV = () => {
    const sectionId = AprSectionId.SectionIV;
    if (!hasSection(sectionId)) return null;

    const sectionResponse = props.response.sectionIV;

    return (
      <Stack spacing="sm">
        <Typography variant="h5">{getSectionName(sectionId)}</Typography>

        <Stack pl={getSpacing("lg")} spacing="xl">
          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentParticipation) && (
            <Box maxWidth={1000}>
              <StudentParticipation
                title={getSectionItemName(AprSectionItemId.StudentParticipation)}
                response={sectionResponse?.studentParticipation || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentParticipationByEthnicBackground) && (
            <Box maxWidth={600}>
              <StudentParticipationByEthnicBackground
                title={getSectionItemName(AprSectionItemId.StudentParticipationByEthnicBackground)}
                response={sectionResponse?.studentParticipationByEthnicBackground || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentParticipationByGender) && (
            <Box maxWidth={600}>
              <StudentParticipationByGender
                title={getSectionItemName(AprSectionItemId.StudentParticipationByGender)}
                response={sectionResponse?.studentParticipationByGender || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentParticipationByLep) && (
            <Box maxWidth={600}>
              <StudentParticipationByLep
                title={getSectionItemName(AprSectionItemId.StudentParticipationByLep)}
                response={sectionResponse?.studentParticipationByLep}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentParticipationByIep) && (
            <Box maxWidth={600}>
              <StudentParticipationByIep
                title={getSectionItemName(AprSectionItemId.StudentParticipationByIep)}
                response={sectionResponse?.studentParticipationByIep}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentsByShelter) && (
            <Box maxWidth={600}>
              <StudentsByShelter
                title={getSectionItemName(AprSectionItemId.StudentsByShelter)!}
                response={sectionResponse?.studentsByShelter || {}}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  };

  const sectionV = () => {
    const sectionId = AprSectionId.SectionV;
    if (!hasSection(sectionId)) return null;

    const sectionResponse = props.response.sectionV;

    return (
      <Stack spacing="sm">
        <Typography variant="h5">{getSectionName(sectionId)}</Typography>

        <Stack pl={getSpacing("lg")} spacing="xl">
          {props.selectedSectionItemIds.includes(AprSectionItemId.ActivitiesAndServices) && (
            <Box>
              <ActivitiesAndServices
                title={getSectionItemName(AprSectionItemId.ActivitiesAndServices)}
                response={sectionResponse?.activitiesAndServices || ({} as AprResponseActivitiesAndServices)}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentsByAnotherFederallyFundedProgram) && (
            <Box maxWidth={600}>
              <StudentsByAnotherFederallyFundedProgram
                title={getSectionItemName(AprSectionItemId.StudentsByAnotherFederallyFundedProgram)}
                response={sectionResponse?.studentsByAnotherFederallyFundedProgram || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.StudentActivitiesAndServices) && (
            <Box maxWidth={1000}>
              <StudentActivitiesAndServices
                title={getSectionItemName(AprSectionItemId.StudentActivitiesAndServices)}
                response={sectionResponse?.studentActivitiesAndServices || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.CoreActivitiesProvidedToFamilyMembers) && (
            <Box maxWidth={1000}>
              <CoreActivitiesProvidedToFamilyMembers
                title={getSectionItemName(AprSectionItemId.CoreActivitiesProvidedToFamilyMembers)}
                response={sectionResponse?.coreActivitiesProvidedToFamilyMembers || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.ProfessionalDevelopmentActivities) && (
            <Box maxWidth={600}>
              <ProfessionalDevelopmentActivities
                title={getSectionItemName(AprSectionItemId.ProfessionalDevelopmentActivities)}
                response={sectionResponse?.professionalDevelopmentActivities || {}}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  };

  const sectionVI = () => {
    const sectionId = AprSectionId.SectionVI;
    if (!hasSection(sectionId)) return null;

    const sectionResponse = props.response.sectionVI;

    return (
      <Stack spacing="sm">
        <Typography variant="h5">{getSectionName(sectionId)}</Typography>

        <Stack pl={getSpacing("lg")} spacing="xl">
          {props.selectedSectionItemIds.includes(AprSectionItemId.AlgebraI) && (
            <Box maxWidth={600}>
              <AlgebraI
                title={getSectionItemName(AprSectionItemId.AlgebraI)!}
                response={sectionResponse?.algebraI || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.AcademicEngagement) && (
            <Box maxWidth={1200}>
              <AcademicEngagement
                title={getSectionItemName(AprSectionItemId.AcademicEngagement)!}
                response={sectionResponse?.academicEngagement || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.FafsaCompletion) && (
            <Box maxWidth={600}>
              <FafsaCompletion
                title={getSectionItemName(AprSectionItemId.FafsaCompletion)!}
                response={sectionResponse?.fafsaCompletion || {}}
              />
            </Box>
          )}

          {props.selectedSectionItemIds.includes(AprSectionItemId.HighSchoolGraduation) && (
            <Box maxWidth={600}>
              <HighSchoolGraduation
                title={getSectionItemName(AprSectionItemId.HighSchoolGraduation)!}
                response={sectionResponse?.highSchoolGraduation || {}}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  };

  const sectionVII = () => {
    const sectionId = AprSectionId.SectionVII;
    if (!hasSection(sectionId)) return null;

    const sectionResponse = props.response.sectionVII;

    return (
      <Stack spacing="sm">
        <Typography variant="h5">{getSectionName(sectionId)}</Typography>

        <Stack pl={getSpacing("lg")} spacing="xl">
          {props.selectedSectionItemIds.includes(AprSectionItemId.PostsecondaryEnrollment) && (
            <Box maxWidth={600}>
              <PostsecondaryEnrollment
                title={getSectionItemName(AprSectionItemId.PostsecondaryEnrollment)!}
                response={sectionResponse?.postsecondaryEnrollment || {}}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Card>
      <Stack spacing="xl">
        {sectionIV()}
        {sectionV()}
        {sectionVI()}
        {sectionVII()}
      </Stack>
    </Card>
  );
};
